import apiClient from './client';

export interface User {
  id: string;
  cognitoUserId: string;
  tenantId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'super_admin' | 'tenant_admin' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  permissions: string[];
  avatarUrl?: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  lastLoginAt?: string;
  invitedAt?: string;
  invitedBy?: string;
  createdAt: string;
  updatedAt: string;
  departments?: Array<{
    id: string;
    name: string;
    departmentRole: string;
  }>;
}

export interface ListUsersParams {
  search?: string;
  status?: string;
  role?: string;
  departmentId?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: 'tenant_admin' | 'manager' | 'user';
  phone?: string;
  jobTitle?: string;
  departmentIds?: string[];
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  role?: 'tenant_admin' | 'manager' | 'user';
  status?: 'active' | 'inactive' | 'suspended';
  phone?: string;
  jobTitle?: string;
  departmentIds?: string[];
  permissions?: string[];
}

class UsersService {
  async listUsers(params: ListUsersParams = {}): Promise<{ users: User[]; total: number }> {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.role) queryParams.append('role', params.role);
    if (params.departmentId) queryParams.append('departmentId', params.departmentId);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await apiClient.get(`/users?${queryParams.toString()}`);
    return response.data;
  }

  async getUser(id: string): Promise<User> {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get('/users/me');
    return response.data;
  }

  async createUser(input: CreateUserInput): Promise<User> {
    const response = await apiClient.post('/users', input);
    return response.data;
  }

  async updateUser(id: string, input: UpdateUserInput): Promise<User> {
    const response = await apiClient.put(`/users/${id}`, input);
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  }
}

export const usersService = new UsersService();
